import React from "react";
import { Link } from "react-router-dom";
import { HeartPulse, Phone, Mail, MapPin, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PitchDeck() {
  return (
    <div className="bg-white text-[#1A1D1A]">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .page-break { page-break-after: always; }
          body { background: white !important; }
        }
      `}</style>

      <div className="no-print sticky top-0 z-50 bg-white border-b border-[#E5EBE7] px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
        <Link to="/" className="flex items-center gap-2" data-testid="pitch-home">
          <div className="w-8 h-8 rounded-lg bg-[#1E4631] grid place-items-center">
            <HeartPulse className="w-4 h-4 text-white"/>
          </div>
          <span className="font-[Outfit] font-bold">KWIKILA Care</span>
        </Link>
        <div className="flex gap-2 flex-wrap">
        <Button onClick={() => window.print()} className="bg-[#1E4631] hover:bg-[#163424] text-white" data-testid="pitch-print">
          <Printer className="w-4 h-4 mr-2"/> Télécharger PDF
        </Button>
        <Link to="/dossier-subventions" data-testid="pitch-grant-link">
          <Button variant="outline" className="border-[#D96C4A] text-[#D96C4A] hover:bg-[#D96C4A] hover:text-white">
            Dossier Subventions
          </Button>
        </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-8 py-12 space-y-12">
        {/* Cover */}
        <section className="page-break min-h-[60vh] flex flex-col justify-center">
          <div className="text-xs uppercase tracking-widest text-[#D96C4A] font-bold mb-4">Dossier de présentation · Partenaires</div>
          <h1 className="text-6xl font-[Outfit] font-bold leading-tight">KWIKILA Care</h1>
          <p className="text-2xl text-[#5C6B61] mt-4 font-[Outfit]">Connecter chaque patient à des soins de qualité, en RDC.</p>
          <div className="mt-12 grid grid-cols-3 gap-8 text-sm">
            <div><div className="text-xs uppercase text-[#5C6B61] font-bold">Secteur</div><div className="mt-1 font-semibold">Healthtech / SaaS</div></div>
            <div><div className="text-xs uppercase text-[#5C6B61] font-bold">Marché</div><div className="mt-1 font-semibold">RD Congo · 100M+ habitants</div></div>
            <div><div className="text-xs uppercase text-[#5C6B61] font-bold">Modèle</div><div className="mt-1 font-semibold">B2B SaaS Multi-hôpitaux</div></div>
          </div>
        </section>

        {/* Mission */}
        <section className="space-y-4">
          <h2 className="text-3xl font-[Outfit] font-bold text-[#1E4631]">1. Mission & Vision</h2>
          <p className="text-[#5C6B61] leading-relaxed text-lg"><strong className="text-[#1A1D1A]">Mission :</strong> permettre à chaque patient en République Démocratique du Congo de bénéficier d'un suivi continu, de conseils santé adaptés et de rappels de rendez-vous accessibles, dès l'instant où il franchit la porte d'un hôpital.</p>
          <p className="text-[#5C6B61] leading-relaxed text-lg"><strong className="text-[#1A1D1A]">Vision :</strong> devenir l'infrastructure numérique de référence du parcours patient en RDC et en Afrique centrale, en s'appuyant sur le téléphone mobile (canal le plus accessible).</p>
        </section>

        {/* Problème */}
        <section className="space-y-4">
          <h2 className="text-3xl font-[Outfit] font-bold text-[#1E4631]">2. Le Problème</h2>
          <ul className="space-y-3 text-[#5C6B61] leading-relaxed text-lg list-disc pl-6">
            <li>Des milliers de patients <strong className="text-[#1A1D1A]">manquent leurs rendez-vous médicaux</strong> faute de rappels accessibles.</li>
            <li>Les <strong>femmes enceintes et enfants 0-3 ans</strong> n'ont pas accès à des conseils continus de santé après l'accouchement.</li>
            <li>Les <strong>personnes âgées</strong> sont isolées dans leur parcours de soins.</li>
            <li>Les hôpitaux n'ont <strong>aucun outil simple</strong> pour communiquer en masse une campagne vaccinale ou une alerte sanitaire.</li>
            <li>Les coordonnées des patients sont consignées sur papier, perdues ou non exploitées.</li>
          </ul>
        </section>

        {/* Solution */}
        <section className="space-y-4 page-break">
          <h2 className="text-3xl font-[Outfit] font-bold text-[#1E4631]">3. Notre Solution</h2>
          <p className="text-[#5C6B61] leading-relaxed text-lg">Une plateforme web utilisée par le secrétariat médical à l'accueil de chaque hôpital partenaire :</p>
          <div className="grid md:grid-cols-2 gap-5 mt-4">
            {[
              ["Enregistrement express", "Le secrétariat saisit en 30 secondes le profil du patient (téléphone, catégorie, langue)."],
              ["3 parcours patient dédiés", "Femmes enceintes & 0-3 ans · Troisième âge · Patients généraux."],
              ["Rappels SMS automatiques", "J-15, J-3 et 45 minutes avant chaque rendez-vous."],
              ["Conseils personnalisés", "Bibliothèque + génération IA en 5 langues nationales."],
              ["Campagnes ciblées", "Alertes vaccinales, prévention, publicité santé."],
              ["Multi-hôpitaux sécurisé", "Chaque hôpital dispose de son espace isolé. Transfert entre hôpitaux avec consentement."],
            ].map(([t, d], i) => (
              <div key={i} className="border border-[#E5EBE7] rounded-xl p-5">
                <div className="font-[Outfit] font-semibold text-lg text-[#1E4631]">{t}</div>
                <div className="text-sm text-[#5C6B61] mt-1">{d}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Marché */}
        <section className="space-y-4">
          <h2 className="text-3xl font-[Outfit] font-bold text-[#1E4631]">4. Le Marché RDC</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[["100M+", "Habitants"], ["75%", "Pénétration mobile"], ["5", "Langues couvertes"], ["~600", "Hôpitaux principaux"]].map(([v, l], i) => (
              <div key={i} className="bg-[#FDFBF7] border border-[#E5EBE7] rounded-xl p-5">
                <div className="text-3xl font-[Outfit] font-bold text-[#1E4631]">{v}</div>
                <div className="text-xs text-[#5C6B61] mt-1 uppercase tracking-wide">{l}</div>
              </div>
            ))}
          </div>
          <p className="text-[#5C6B61] leading-relaxed text-lg mt-4">La RDC est le 2ᵉ pays d'Afrique par sa population, avec un taux de natalité parmi les plus élevés du continent. Plus de 75% des Congolais possèdent un téléphone mobile, ce qui rend le SMS le canal de communication le plus universel — bien plus que l'internet ou les apps.</p>
        </section>

        {/* Business */}
        <section className="space-y-4 page-break">
          <h2 className="text-3xl font-[Outfit] font-bold text-[#1E4631]">5. Modèle économique</h2>
          <p className="text-[#5C6B61] leading-relaxed text-lg">Abonnement SaaS B2B par hôpital :</p>
          <div className="grid md:grid-cols-4 gap-4 mt-4">
            {[
              ["Essai", "0 USD", "30 jours · 50 patients · 100 SMS"],
              ["Basic", "80 USD/mois", "1 médecin · 500 patients · 1 000 SMS/mois"],
              ["Pro", "200 USD/mois", "5 médecins · rapports DPS + PDF · campagnes SMS · formation · import CSV"],
              ["Premium", "400 USD/mois", "Médecins illimités · certificats · backup CSV hebdo · support 24h"],
            ].map(([n, p, d], i) => (
              <div key={i} className={`rounded-xl p-6 ${i === 2 ? "bg-[#1E4631] text-white" : "border border-[#E5EBE7]"}`}>
                <div className="font-[Outfit] font-bold text-xl">{n}</div>
                <div className="text-3xl font-[Outfit] font-bold mt-2">{p}</div>
                <div className={`text-sm mt-2 ${i === 2 ? "text-white/80" : "text-[#5C6B61]"}`}>{d}</div>
              </div>
            ))}
          </div>
          <p className="text-[#5C6B61] leading-relaxed mt-4 text-sm"><strong>Projection conservatrice 3 ans :</strong> Année 1 : 20 hôpitaux × 80$ = 19 K$ ARR · Année 2 : 100 hôpitaux × 120$ (mix Basic/Pro) = 144 K$ ARR · Année 3 : 250 hôpitaux × 160$ = 480 K$ ARR.</p>
        </section>

        {/* Roadmap */}
        <section className="space-y-4">
          <h2 className="text-3xl font-[Outfit] font-bold text-[#1E4631]">6. Feuille de route</h2>
          <ul className="space-y-2 text-[#5C6B61] text-lg">
            <li><strong className="text-[#1A1D1A]">M1-M3 :</strong> 3-5 hôpitaux pilotes Kinshasa, intégration SMS opérateur local.</li>
            <li><strong className="text-[#1A1D1A]">M4-M6 :</strong> Extension à Lubumbashi, Goma, Kisangani. Premiers payants.</li>
            <li><strong className="text-[#1A1D1A]">M7-M12 :</strong> 100 hôpitaux. App mobile patient (iOS/Android).</li>
            <li><strong className="text-[#1A1D1A]">An 2 :</strong> 5 langues complètes, agrément ministère, scaling Afrique centrale.</li>
          </ul>
        </section>

        {/* Contact */}
        <section className="space-y-4">
          <h2 className="text-3xl font-[Outfit] font-bold text-[#1E4631]">7. Nous rejoindre</h2>
          <p className="text-[#5C6B61] text-lg">Nous recherchons : hôpitaux pilotes, partenaires institutionnels (Ministère de la Santé Publique, ONG, OMS, UNICEF, USAID), investisseurs early-stage (santé digitale Afrique), conseillers médicaux.</p>
          <div className="flex flex-wrap gap-6 text-[#1A1D1A] mt-6 text-base">
            <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4 text-[#D96C4A]"/> +243 824 314 459</span>
            <span className="inline-flex items-center gap-2"><Mail className="w-4 h-4 text-[#D96C4A]"/> contact@kwikilacare.com</span>
            <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-[#D96C4A]"/> Kinshasa, RDC</span>
          </div>
        </section>

        <div className="border-t border-[#E5EBE7] pt-6 text-center text-xs text-[#5C6B61]">© 2026 KWIKILA Care — Tous droits réservés. Conçu pour la République Démocratique du Congo.</div>
      </div>
    </div>
  );
}
